import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import { checkBingoWins } from '../../widgets/bingo/checkBingoWins';
import { generateBingoCard } from '../../widgets/bingo/generateBingoCard';
import {
  DEFAULT_BINGO_PAYOUTS,
  type BingoCard,
  type BingoPayoutMap,
  type BingoWin,
} from '../../widgets/bingo/types';

export interface BingoGameModelConfig {
  startingBalance: number;
  buyIn: number;
  maxCallsPerGame: number;
  payouts?: BingoPayoutMap;
}

export type BingoBalanceChangeReason = 'buy-in' | 'win';

export type BingoGameEvent =
  | { kind: 'balance-changed'; before: number; after: number; reason: BingoBalanceChangeReason }
  | { kind: 'not-enough-coins'; required: number; have: number }
  | { kind: 'game-started'; card: BingoCard }
  | { kind: 'number-called'; number: number; callsMade: number; maxCalls: number }
  | { kind: 'cell-marked'; col: number; row: number; number: number }
  | { kind: 'bingo-won'; win: BingoWin; winnings: number }
  | { kind: 'game-ended'; reason: string };

/**
 * Authoritative state for the bingo demo: balance, current card, marked grid,
 * number pool, calls counter, won-this-game flag.
 *
 * <p>The view-side auto-call ticker (call every N ms) is the scene's job —
 * the model exposes {@link callNext} which the scene invokes on each tick
 * threshold. Cell tapping flows through {@link tryMarkCell}; the model
 * checks for wins via the existing pure {@link checkBingoWins} function on
 * every successful mark.
 */
export class BingoGameModel {
  public readonly events = new EventEmitter<BingoGameEvent>();
  public readonly buyIn: number;
  public readonly maxCallsPerGame: number;
  private readonly payouts: BingoPayoutMap;

  private _balance: number;
  private _gameActive = false;
  private _card: BingoCard | null = null;
  private _marked: boolean[][] = [];
  private _numberPool: number[] = [];
  private _callsMade = 0;
  private _wonThisGame: BingoWin | null = null;

  constructor(config: BingoGameModelConfig) {
    this._balance = config.startingBalance;
    this.buyIn = config.buyIn;
    this.maxCallsPerGame = config.maxCallsPerGame;
    this.payouts = config.payouts ?? DEFAULT_BINGO_PAYOUTS;
  }

  get balance(): number {
    return this._balance;
  }

  get gameActive(): boolean {
    return this._gameActive;
  }

  get card(): BingoCard | null {
    return this._card;
  }

  get callsMade(): number {
    return this._callsMade;
  }

  get wonThisGame(): BingoWin | null {
    return this._wonThisGame;
  }

  /** Whether the given cell has been marked. */
  isMarked(col: number, row: number): boolean {
    return this._marked[col]?.[row] ?? false;
  }

  /** Try to start a new game. Debits the buy-in, generates a card, fills the
   * pool, marks the FREE square. Returns true on success. Emits
   * {@code not-enough-coins} on failure or false if a game is already
   * active. */
  tryStartGame(random: Random): boolean {
    if (this._gameActive) return false;
    if (this._balance < this.buyIn) {
      this.events.emit({
        kind: 'not-enough-coins',
        required: this.buyIn,
        have: this._balance,
      });
      return false;
    }
    const before = this._balance;
    this._balance -= this.buyIn;
    this.events.emit({
      kind: 'balance-changed',
      before,
      after: this._balance,
      reason: 'buy-in',
    });

    const card = generateBingoCard(random);
    this._card = card;
    this._marked = Array.from({ length: 5 }, () => Array<boolean>(5).fill(false));
    this._marked[2]![2] = true; // FREE square pre-marked.
    this._numberPool = [];
    for (let n = 1; n <= 75; n++) this._numberPool.push(n);
    this._callsMade = 0;
    this._wonThisGame = null;
    this._gameActive = true;

    this.events.emit({ kind: 'game-started', card });
    return true;
  }

  /** Pull a random number from the remaining pool. Returns it, or null if
   * pool empty (in which case the model auto-ends the game). */
  callNext(random: Random): number | null {
    if (!this._gameActive) return null;
    if (this._numberPool.length === 0 || this._callsMade >= this.maxCallsPerGame) {
      this.endGame('out of calls');
      return null;
    }
    const idx = random.nextIntBetween(0, this._numberPool.length);
    const num = this._numberPool.splice(idx, 1)[0]!;
    this._callsMade += 1;
    this.events.emit({
      kind: 'number-called',
      number: num,
      callsMade: this._callsMade,
      maxCalls: this.maxCallsPerGame,
    });
    return num;
  }

  /** Try to mark a cell. Succeeds only if the game is active, the cell is
   * not already marked, and the number was previously called (i.e. no
   * longer in the pool). Triggers a {@code bingo-won} event + game end if
   * the mark completes a winning line. Returns true on successful mark. */
  tryMarkCell(col: number, row: number, number: number): boolean {
    if (!this._gameActive) return false;
    if (this._marked[col]![row]) return false;
    // Number must have been called — i.e. is no longer in the pool.
    if (this._numberPool.includes(number)) return false;

    this._marked[col]![row] = true;
    this.events.emit({ kind: 'cell-marked', col, row, number });

    const wins = checkBingoWins(this._marked, this.payouts);
    if (wins.length > 0) {
      const win = wins[0]!;
      this._wonThisGame = win;
      const winnings = this.buyIn * win.multiplier;
      const before = this._balance;
      this._balance += winnings;
      this.events.emit({
        kind: 'balance-changed',
        before,
        after: this._balance,
        reason: 'win',
      });
      this.events.emit({ kind: 'bingo-won', win, winnings });
      this.endGame(win.kind);
    }
    return true;
  }

  endGame(reason: string): void {
    if (!this._gameActive) return;
    this._gameActive = false;
    this.events.emit({ kind: 'game-ended', reason });
  }

  dispose(): void {
    this.events.clear();
  }
}
